import { CompareAndSwapResult, StorageApi } from './StorageApi.ts'
import { ValidatingStorageApi } from './ValidatingStorageApi'

export type StorageType = 'deviceCache' | 'appStorage' | 'ownerStorage'

const STORAGE_PREFIXES: Record<StorageType, string> = {
  deviceCache: 'rundotGame:deviceCache',
  appStorage: 'rundotGame:appStorage',
  ownerStorage: 'rundotGame:ownerStorage',
}

export function createMockStorageApi(
  storageType: StorageType,
  appUrl?: string,
): StorageApi {
  const appIdentifier = appUrl ? generateAppIdentifier(appUrl) : null

  let prefix = STORAGE_PREFIXES[storageType]
  let syncDelay = 0 // Simulate sync delay for cloud storage

  switch (storageType) {
    case 'deviceCache':
      syncDelay = 0 // No sync delay
      break
    case 'appStorage':
    case 'ownerStorage':
      syncDelay = 100 // Simulate cloud sync delay
      break
    default:
      throw new Error(`Unknown storage type: ${storageType}`)
  }

  prefix = !appIdentifier
    ? `${prefix}:`
    : `${prefix}:${appIdentifier}:`

  const api = new MockStorageApi(prefix, syncDelay)
  // deviceCache is device-local (localStorage) and unvalidated in prod; every
  // other scope is server-backed and must reject what the server rejects.
  return storageType === 'deviceCache' ? api : new ValidatingStorageApi(api)
}

export class MockStorageApi implements StorageApi {
  private readonly prefix: string
  private readonly syncDelay: number
  private readonly orderStorageKey: string

  constructor(prefix: string, syncDelay: number) {
    this.prefix = prefix
    this.syncDelay = syncDelay
    this.orderStorageKey = `${prefix}__order__`
  }

  async clear(): Promise<void> {
    const keysToRemove: string[] = []
    const fullLength = localStorage.length
    for (let i = 0; i < fullLength; i++) {
      const fullKey = localStorage.key(i)
      if (!fullKey || fullKey === this.orderStorageKey) {
        continue
      }
      if (fullKey.startsWith(this.prefix)) {
        keysToRemove.push(fullKey)
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key)
    }
    this.clearOrder()
    await this.simulateSyncDelay()
  }

  async getAllItems(): Promise<string[]> {
    const items = new Array<string>()
    const orderedKeys = this.keys()
    for (const key of orderedKeys) {
      const value = localStorage.getItem(this.buildKey(key))
      if (value !== null) {
        items.push(key)
      } else {
        this.removeFromOrder(key)
      }
    }
    return items
  }

  async getAllData(): Promise<Record<string, string>> {
    const result: Record<string, string> = {}
    const orderedKeys = this.keys()
    for (const key of orderedKeys) {
      const value = localStorage.getItem(this.buildKey(key))
      if (value !== null) {
        result[key] = value
      } else {
        this.removeFromOrder(key)
      }
    }
    return result
  }

  async getItem(key: string): Promise<string | null> {
    const fullKey = this.buildKey(key)
    await this.simulateSyncDelay()
    return localStorage.getItem(fullKey)
  }

  async key(index: number): Promise<string | null> {
    const keys = this.keys()
    if (index < 0 || index >= keys.length) {
      return null
    }
    await this.simulateSyncDelay()
    return keys[index]
  }

  async length(): Promise<number> {
    return this.keys().length
  }

  async removeItem(key: string): Promise<void> {
    const fullKey = this.buildKey(key)
    await this.simulateSyncDelay()
    localStorage.removeItem(fullKey)
    this.removeFromOrder(key)
  }

  async setItem(key: string, item: string): Promise<void> {
    const fullKey = this.buildKey(key)
    await this.simulateSyncDelay()
    localStorage.setItem(fullKey, item)
    this.upsertOrder(key)
  }

  async setMultipleItems(
    entries: { key: string; value: string }[],
  ): Promise<void> {
    for (const entry of entries) {
      const fullKey = this.buildKey(entry.key)
      localStorage.setItem(fullKey, entry.value)
    }
    await this.simulateSyncDelay()
    this.bulkUpsertOrder(entries.map((entry) => entry.key))
  }

  async removeMultipleItems(keys: string[]): Promise<void> {
    for (const key of keys) {
      const fullKey = this.buildKey(key)
      localStorage.removeItem(fullKey)
    }
    await this.simulateSyncDelay()
    this.bulkRemoveFromOrder(keys)
  }

  // localStorage is synchronous, so the compare-and-write is naturally atomic.
  async compareAndSwap(
    key: string,
    expectedValue: string | null,
    nextValue: string | null,
  ): Promise<CompareAndSwapResult> {
    const fullKey = this.buildKey(key)
    await this.simulateSyncDelay()
    const currentValue = localStorage.getItem(fullKey)
    if (currentValue !== expectedValue) {
      return { swapped: false, currentValue }
    }
    if (nextValue === null) {
      localStorage.removeItem(fullKey)
      this.removeFromOrder(key)
    } else {
      localStorage.setItem(fullKey, nextValue)
      this.upsertOrder(key)
    }
    return { swapped: true }
  }

  async getMultipleItems(keys: string[]): Promise<Record<string, string>> {
    const result: Record<string, string> = {}
    for (const key of this.dedupeKeys(keys)) {
      const value = localStorage.getItem(this.buildKey(key))
      if (value !== null) {
        result[key] = value
      }
    }
    return result
  }

  private buildKey(key: string): string {
    const prefix = this.prefix
    return `${prefix}${key}`
  }

  private extractKey(fullKey: string): string {
    const prefix = this.prefix
    return fullKey.substring(prefix.length)
  }

  private keys(): string[] {
    const order = this.readOrder()
    return [...order]
  }

  private async simulateSyncDelay() {
    const syncDelay = this.syncDelay
    if (syncDelay > 0) {
      await new Promise((resolve) => setTimeout(resolve, syncDelay))
    }
  }

  private readOrder(): string[] {
    const raw = localStorage.getItem(this.orderStorageKey)
    if (!raw) {
      return this.rebuildOrderFromStorage()
    }
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        return this.normalizeOrder(parsed)
      }
    } catch {
      // Fall through to rebuild order below
    }
    return this.rebuildOrderFromStorage()
  }

  private normalizeOrder(order: unknown[]): string[] {
    const seen = new Set<string>()
    const normalized: string[] = []
    let changed = false

    for (const entry of order) {
      if (typeof entry !== 'string') {
        changed = true
        continue
      }
      if (seen.has(entry)) {
        changed = true
        continue
      }
      const fullKey = this.buildKey(entry)
      if (localStorage.getItem(fullKey) === null) {
        changed = true
        continue
      }
      seen.add(entry)
      normalized.push(entry)
    }

    if (changed) {
      this.writeOrder(normalized)
    }

    return normalized
  }

  private rebuildOrderFromStorage(): string[] {
    const keys: string[] = []
    const total = localStorage.length
    for (let i = 0; i < total; i++) {
      const fullKey = localStorage.key(i)
      if (!fullKey) continue
      if (fullKey === this.orderStorageKey) continue
      if (fullKey.startsWith(this.prefix)) {
        keys.push(this.extractKey(fullKey))
      }
    }
    this.writeOrder(keys)
    return keys
  }

  private upsertOrder(key: string) {
    const order = this.readOrder()
    const index = order.indexOf(key)
    if (index !== -1) {
      order.splice(index, 1)
    }
    order.push(key)
    this.writeOrder(order)
  }

  private bulkUpsertOrder(keys: string[]) {
    const dedupedKeys = this.dedupeKeys(keys)
    if (dedupedKeys.length === 0) {
      return
    }
    const order = this.readOrder()
    const keysSet = new Set(dedupedKeys)
    const filtered = order.filter((entry) => !keysSet.has(entry))
    for (const key of dedupedKeys) {
      filtered.push(key)
    }
    this.writeOrder(filtered)
  }

  private removeFromOrder(key: string) {
    const order = this.readOrder()
    const index = order.indexOf(key)
    if (index !== -1) {
      order.splice(index, 1)
      this.writeOrder(order)
    }
  }

  private bulkRemoveFromOrder(keys: string[]) {
    const dedupedKeys = this.dedupeKeys(keys)
    if (dedupedKeys.length === 0) {
      return
    }
    const order = this.readOrder()
    const keysSet = new Set(dedupedKeys)
    const filtered = order.filter((entry) => !keysSet.has(entry))
    if (filtered.length !== order.length) {
      this.writeOrder(filtered)
    }
  }

  private writeOrder(order: string[]) {
    if (order.length === 0) {
      localStorage.removeItem(this.orderStorageKey)
      return
    }
    localStorage.setItem(this.orderStorageKey, JSON.stringify(order))
  }

  private clearOrder() {
    localStorage.removeItem(this.orderStorageKey)
  }

  private dedupeKeys(keys: string[]): string[] {
    const result: string[] = []
    const seen = new Set<string>()
    for (const key of keys) {
      if (typeof key !== 'string') {
        continue
      }
      if (seen.has(key)) {
        continue
      }
      seen.add(key)
      result.push(key)
    }
    return result
  }
}

function generateAppIdentifier(appUrl?: string) {
  if (!appUrl) appUrl = ''

  // First normalize to ensure we're hashing the same identifier regardless of format
  const normalizedId = normalizeAppIdentifier(appUrl)
  // Generating hash for normalized ID

  let hash = 0
  for (let i = 0; i < normalizedId.length; i++) {
    const char = normalizedId.charCodeAt(i)
    hash = (hash << 5) - hash + char // Same as real API - djb2 hash algorithm
    hash |= 0 // Convert to 32bit integer
  }

  // Convert to hex
  // Generated app identifier
  return Math.abs(hash).toString(16)
}

function normalizeAppIdentifier(input: string) {
  // Normalizing app identifier input

  // Handle full URLs
  if (input.startsWith('http')) {
    try {
      // For URLs like http://127.0.0.1:5000/H5/bird-flap/index.html?cacheBust=12345678
      // Extract the app ID part (bird-flap)
      const url = new URL(input)
      const pathParts = url.pathname.split('/')

      // Find the part after 'H5' in the path
      const h5Index = pathParts.findIndex((part) => part === 'H5')
      if (h5Index !== -1 && h5Index < pathParts.length - 1) {
        const appId = pathParts[h5Index + 1]
        // Extracted app ID from full URL path
        return appId
      }

      // If we couldn't extract from path, use the hostname as fallback
      // Could not extract app ID from URL path, using hostname
      return url.hostname
    } catch (error) {
      console.error(`[RUN:mock] Error parsing URL: ${error}`, { input })
      // If URL parsing fails, treat as a raw ID
      return input
    }
  }

  // Handle path-based URLs like '/H5/bird-flap/index.html'
  if (input.includes('/H5/')) {
    try {
      const pathParts = input.split('/')
      // Find the part after 'H5' in the path
      const h5Index = pathParts.findIndex((part) => part === 'H5')
      if (h5Index !== -1 && h5Index < pathParts.length - 1) {
        const appId = pathParts[h5Index + 1]
        // Extracted app ID from path-based URL
        return appId
      }
    } catch (error) {
      console.error(`[RUN:mock] Error parsing path-based URL: ${error}`, {
        input,
      })
    }
  }

  // Not a URL, just return as is
  // Input is not a URL, treating as raw app ID
  return input
}
